/**
 * PropTypes shapes for SideNav: nav items, viewing/current view section, teams, pinned items.
 */
import PropTypes from "prop-types";
import { BADGE_VARIANTS, ITEM_TYPES } from "../constants/sideNav";

export const NavigationItemShape = {
  iconName: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  badgeNumber: PropTypes.string,
  badgeDot: PropTypes.bool,
  hide: PropTypes.bool,
  large: PropTypes.bool,
  bottomAligned: PropTypes.bool,
  actionType: PropTypes.oneOf(Object.values(ITEM_TYPES)).isRequired,
  component: PropTypes.elementType,
  link: PropTypes.string,
  onClick: PropTypes.func,
  isActive: PropTypes.bool,
  pageSpecific: PropTypes.bool,
  isExpandedByDefault: PropTypes.bool,
};

export const ViewingSectionShape = {
  teamName: PropTypes.string.isRequired,
  avatar: PropTypes.string,
  shape: PropTypes.string,
  teamLink: PropTypes.string.isRequired,
  teamType: PropTypes.string,
  badgeVariant: PropTypes.oneOf(BADGE_VARIANTS),
  subNav: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
};

/**
 * Shape for entries passed via `<SideNav yourTeams=...>`. Beyond the
 * minimal `name` / `link`, every other field is optional enrichment
 * consumed by `SideNavTeamsSection`:
 *
 * - `gradient`: fallback avatar gradient
 * - `type`: human-readable team-type label (drives the bar visibility)
 * - `badgeVariant`: theme-token key for the `TeamTypeBar` colour
 * - `popoverBadge`: ReactNode rendered alongside the name in the tooltip
 * - `id`: stable React key (falls back to `name`)
 */
export const TeamShape = {
  avatar: PropTypes.string,
  name: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  gradient: PropTypes.string,
  type: PropTypes.string,
  badgeVariant: PropTypes.string,
  popoverBadge: PropTypes.node,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/**
 * Shape for entries passed via `<SideNav pinnedItems=...>`. As with
 * `TeamShape`, beyond `name` / `link` every field is optional. Adds:
 *
 * - `shape`: avatar shape (e.g. "square" / "circle" / "hexagon" / "tag")
 * - `entityType`: drives team-type-bar gating in the rendered row
 * - `onUnpin`: presence triggers the inline unpin button
 */
export const PinnedItemShape = {
  avatar: PropTypes.string,
  name: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  shape: PropTypes.string,
  gradient: PropTypes.string,
  entityType: PropTypes.oneOf(["team", "person", "tag"]),
  type: PropTypes.string,
  badgeVariant: PropTypes.string,
  popoverBadge: PropTypes.node,
  onUnpin: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const SubNavItemShape = {
  name: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
};

export const CurrentViewSectionShape = {
  name: PropTypes.string.isRequired,
  avatar: PropTypes.string,
  shape: PropTypes.string,
  badge: PropTypes.node,
  subNav: PropTypes.arrayOf(SubNavItemShape),
};
